// src/pages/admin/BusinessCollection.js - REFACTORED VERSION - RESPONSIVE
import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GetAllBusinessesAction,
  AddBusinessAction,
  GetBusinessInteractionLogAction,
  AddBusinessLogAction
} from "../../actions/BusinessAction";
import {
  GetAllLicensesAction
} from "../../actions/LicenseAction";
import {
  GetAllClientsAction
} from "../../actions/ClientAction";
import {
  GetPaymentByIdAction,
  GetPaymentDetailsByIdAction,
  GetAllPaymentsAction
} from "../../actions/PaymentAction";

// Import selectors from reducer 
import {
  selectBusinessesData,
  selectBusinessesLoading,
  selectBusinessesError,
  selectAddBusinessData,
  selectAddBusinessLoading,
  selectAddBusinessError,
  selectAllLicensesData,
  selectAllLicensesLoading,
  selectAllLicensesError,
  selectAllSystemsData,
  selectAllClientsData,
  selectPaymentByIdData,
  selectPaymentByIdLoading,
  selectPaymentByIdError,
  selectPaymentDetailsData,
  selectPaymentDetailsLoading,
  selectPaymentDetailsError,
  selectAllPaymentsData,
  selectBusinessLogData,
  selectBusinessLogLoading,
  selectBusinessLogError,
  selectAddBusinessLogData,
  selectAddBusinessLogLoading,
  selectAddBusinessLogError
} from "../../reducers/BusinessReducer"; 

import AddBusinessModal from '../../components/modals/AddBusinessModal';
import BusinessLicensesView from '../../components/views/BusinessLicensesView';
import BusinessLogView from '../../components/views/BusinessLogView';
import PaymentInformationView from '../../components/views/PaymentInformationView';
import BusinessTable from '../../components/tables/BusinessTable';

const BusinessCollection = () => {
  const dispatch = useDispatch();

  const businessesData = useSelector(selectBusinessesData);
  const businessesLoading = useSelector(selectBusinessesLoading);
  const businessesError = useSelector(selectBusinessesError);

  const addBusinessResult = useSelector(selectAddBusinessData);
  const addBusinessLoading = useSelector(selectAddBusinessLoading);
  const addBusinessError = useSelector(selectAddBusinessError);

  const allLicensesData = useSelector(selectAllLicensesData);
  const allLicensesLoading = useSelector(selectAllLicensesLoading);
  const allLicensesError = useSelector(selectAllLicensesError);

  const systemsData = useSelector(selectAllSystemsData);
  const clientsData = useSelector(selectAllClientsData);

  const paymentByIdData = useSelector(selectPaymentByIdData);
  const paymentByIdLoading = useSelector(selectPaymentByIdLoading);
  const paymentByIdError = useSelector(selectPaymentByIdError);

  const paymentDetailsData = useSelector(selectPaymentDetailsData);
  const paymentDetailsLoading = useSelector(selectPaymentDetailsLoading);
  const paymentDetailsError = useSelector(selectPaymentDetailsError);

  const allPaymentsData = useSelector(selectAllPaymentsData);

  const logsData = useSelector(selectBusinessLogData);
  const logsLoadingState = useSelector(selectBusinessLogLoading);
  const logsErrorState = useSelector(selectBusinessLogError);

  const addLogResult = useSelector(selectAddBusinessLogData);
  const addLogLoading = useSelector(selectAddBusinessLogLoading);
  const addLogError = useSelector(selectAddBusinessLogError);

  // State variables
  const [businesses, setBusinesses] = useState([]);
  const [systems, setSystems] = useState([]);
  const [clients, setClients] = useState([]);
  const [allLicenses, setAllLicenses] = useState([]);
  const [businessLicenses, setBusinessLicenses] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showLicenses, setShowLicenses] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [duplicateNameError, setDuplicateNameError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  
  // Business Log state
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // New business form state
  const [newBusiness, setNewBusiness] = useState({
    business_name: '',
    industry_type: '',
    client_id: ''
  });

  // Load all data on component mount
  useEffect(() => {
    loadBusinesses();
  //  loadSystems();
    loadClients();
    loadAllLicenses();
    loadAllPayments();
  }, []);

  // Format and set businesses data
  useEffect(() => {
    if (businessesData) {
      formatAndSetBusinesses(businessesData);
    }
  }, [businessesData, clients, systems, allLicenses]);

  // Format and set systems data
  useEffect(() => {
    if (systemsData) {
      formatAndSetSystems(systemsData);
    }
  }, [systemsData]);

  // Format and set clients data
  useEffect(() => {
    if (clientsData) {
      formatAndSetClients(clientsData);
    }
  }, [clientsData]);

  // Format and set all licenses
  useEffect(() => {
    if (allLicensesData) {
      formatAndSetAllLicenses(allLicensesData);
    }
  }, [allLicensesData]);

  // Format and set all payments
  useEffect(() => {
    if (allPaymentsData) {
      formatAndSetAllPayments(allPaymentsData);
    }
  }, [allPaymentsData]);

  // Handle successful business addition
  useEffect(() => {
    if (addBusinessResult && !addBusinessLoading) {
      console.log('✅ Add Business Success - Result:', addBusinessResult);
      
      const isSuccess = addBusinessResult.success !== false;
      
      if (isSuccess) {
        setShowAddForm(false);
        setNewBusiness({
          business_name: '',
          industry_type: '',
          client_id: ''
        });
        setDuplicateNameError('');
        setValidationErrors({});
        
        // Reload businesses
        setTimeout(() => {
          loadBusinesses();
        }, 1000);
      } else {
        // Check for duplicate error from backend
        if (addBusinessError && addBusinessError.toLowerCase().includes('duplicate')) {
          setDuplicateNameError('Business name already exists. Please use a different name.');
        }
      }
    }
  }, [addBusinessResult, addBusinessLoading]);

  // Handle business addition error
  useEffect(() => {
    if (addBusinessError && !addBusinessLoading) {
      console.error('❌ Add Business Error:', addBusinessError);
    }
  }, [addBusinessError, addBusinessLoading]);

  // Handle payment data
  useEffect(() => {
    if (paymentByIdData) {
      console.log('💰 Payment Master Data:', paymentByIdData);
      setPaymentData(paymentByIdData);
    }
  }, [paymentByIdData]);

  // Handle payment details data
  useEffect(() => {
    if (paymentDetailsData) {
      console.log('💰 Payment Details Data:', paymentDetailsData);
      formatPaymentDetails(paymentDetailsData);
    }
  }, [paymentDetailsData]);

  // Handle logs data
  useEffect(() => {
    if (logsData) {
      console.log('📊 Logs data received:', logsData);
      formatAndSetLogs(logsData);
      
      // Filter logs by selected business
      if (selectedBusiness) {
        filterLogsByBusiness(selectedBusiness.business_name, logsData);
      }
    }
  }, [logsData, selectedBusiness]);

  // Handle log addition success
  useEffect(() => {
    if (addLogResult && !addLogLoading) {
      console.log('✅ Add log success:', addLogResult);
      
      if (addLogResult.success !== false && selectedBusiness) {
        // Create a new log object from the form data and API response
        let newLog = null;
        
        if (Array.isArray(addLogResult.ResultSet) && addLogResult.ResultSet.length > 0) {
          newLog = addLogResult.ResultSet[0];
        } else if (Array.isArray(addLogResult.resultSet) && addLogResult.resultSet.length > 0) {
          newLog = addLogResult.resultSet[0];
        } else if (addLogResult.log_id) {
          newLog = addLogResult;
        } else if (addLogResult.Result && Array.isArray(addLogResult.Result) && addLogResult.Result.length > 0) {
          newLog = addLogResult.Result[0];
        }
        
        // Format the new log to match existing logs
        if (newLog) {
          const formattedLog = {
            log_id: newLog.log_id || newLog.sbl_log_id || newLog.id || `log-${Date.now()}`,
            client_id: newLog.client_id || newLog.sbl_client_id || newLog.scm_client_id || selectedBusiness.client_id,
            client_name: newLog.client_name || newLog.scm_client_name || 'Unknown Client',
            business_id: newLog.business_id || newLog.sbl_business_id || newLog.sbm_business_id || selectedBusiness.business_id,
            business_name: newLog.business_name || newLog.sbm_business_name || selectedBusiness.business_name,
            contact_person: newLog.contact_person || newLog.sbl_contact_person || '',
            log_date: newLog.log_date || newLog.sbl_log_date || new Date().toISOString().split('T')[0],
            log_type: newLog.log_type || newLog.sbl_log_type || 'general',
            log_subject: newLog.log_subject || newLog.sbl_log_subject || '',
            log_notes: newLog.log_notes || newLog.sbl_log_notes || ''
          };
          
          // Add the new log to filteredLogs immediately
          setFilteredLogs(prevLogs => [formattedLog, ...prevLogs]);
          setLogs(prevLogs => [formattedLog, ...prevLogs]);
        } else {
          // If we can't extract the new log from response, reload logs
          loadBusinessLogs();
        }
      }
    }
  }, [addLogResult, addLogLoading]);

  // Handle log addition error
  useEffect(() => {
    if (addLogError && !addLogLoading) {
      console.error('❌ Add log error:', addLogError);
      setLogsError(addLogError);
    }
  }, [addLogError, addLogLoading]);

  // Check for duplicate business name
  const checkDuplicateBusinessName = (businessName) => {
    if (!businessName.trim()) return false;
    return businesses.some(business => 
      business.business_name.toLowerCase() === businessName.toLowerCase()
    );
  };

  // Validate business form inputs
  const validateBusinessForm = () => {
    const errors = {};
    
    // Check business name
    if (!newBusiness.business_name.trim()) {
      errors.business_name = 'Business name is required';
    } else if (checkDuplicateBusinessName(newBusiness.business_name)) {
      errors.business_name = 'Business name already exists. Please use a different name.';
    } else if (/^\d+$/.test(newBusiness.business_name.trim())) {
      errors.business_name = 'Business name cannot be only numbers';
    } else if (newBusiness.business_name.trim().length < 2) {
      errors.business_name = 'Business name must be at least 2 characters';
    }
    
    // Check industry type
    if (!newBusiness.industry_type.trim()) {
      errors.industry_type = 'Industry type is required';
    } else if (/^\d+$/.test(newBusiness.industry_type.trim())) {
      errors.industry_type = 'Industry type cannot be only numbers';
    }
    
    // Check client selection
    if (!newBusiness.client_id) {
      errors.client_id = 'Please select a client';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filter logs by business name
  const filterLogsByBusiness = (businessName, logsData) => {
    if (!businessName || !logsData) {
      setFilteredLogs([]);
      return;
    }
    
    console.log(`🔍 Filtering logs for business: ${businessName}`);
    
    // First, format the logs
    const formattedLogs = formatLogsForFiltering(logsData);
    
    // Then filter by business name
    const filtered = formattedLogs.filter(log => 
      log.business_name.toLowerCase() === businessName.toLowerCase()
    );
    
    console.log(`✅ Found ${filtered.length} logs for business "${businessName}"`);
    setFilteredLogs(filtered);
  };

  // Format logs for filtering
  const formatLogsForFiltering = (data) => {
    let logsArray = [];

    if (Array.isArray(data)) {
      logsArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) logsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) logsArray = data.resultSet;
      else if (Array.isArray(data.data)) logsArray = data.data;
      else if (Array.isArray(data.responseBody)) logsArray = data.responseBody;
      else if (Array.isArray(data.logs)) logsArray = data.logs;
      else if (data.log_id) logsArray = [data];
      else if (data.Result && Array.isArray(data.Result)) logsArray = data.Result;
      else if (data.ResultSet && typeof data.ResultSet === 'object') {
        logsArray = [data.ResultSet];
      }
    }

    return logsArray.map((log, index) => ({
      log_id: log.log_id || log.sbl_log_id || log.id || `log-${Date.now()}-${index}`,
      client_id: log.client_id || log.sbl_client_id || log.scm_client_id || '',
      client_name: log.client_name || log.scm_client_name || 'Unknown Client',
      business_id: log.business_id || log.sbl_business_id || log.sbm_business_id || '',
      business_name: log.business_name || log.sbm_business_name || 'Unknown Business',
      contact_person: log.contact_person || log.sbl_contact_person || 'Not specified',
      log_date: log.log_date || log.sbl_log_date || new Date().toISOString().split('T')[0],
      log_type: log.log_type || log.sbl_log_type || 'general',
      log_subject: log.log_subject || log.sbl_log_subject || 'No Subject',
      log_notes: log.log_notes || log.sbl_log_notes || 'No notes available'
    }));
  };

  // Format and set businesses
  const formatAndSetBusinesses = (data) => {
    console.log('🔄 Formatting businesses data:', data);
    
    let businessesArray = [];

    if (Array.isArray(data)) {
      businessesArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) businessesArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) businessesArray = data.resultSet;
      else if (Array.isArray(data.data)) businessesArray = data.data;
      else if (Array.isArray(data.responseBody)) businessesArray = data.responseBody;
      else if (Array.isArray(data.businesses)) businessesArray = data.businesses;
      else if (data.business_id) businessesArray = [data];
      else if (data.ResultSet && typeof data.ResultSet === 'object') {
        businessesArray = [data.ResultSet];
      }
    }

    // Filter out duplicates based on business name
    const uniqueBusinesses = businessesArray.reduce((acc, current) => {
      const isDuplicate = acc.some(item => 
        item.business_name.toLowerCase() === current.business_name?.toLowerCase()
      );
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, []);

    // Sort by business_id to maintain sequence
    uniqueBusinesses.sort((a, b) => {
      const idA = parseInt(a.business_id || a.BusinessID || a.id || 0);
      const idB = parseInt(b.business_id || b.BusinessID || b.id || 0);
      return idA - idB;
    });

    const formatted = uniqueBusinesses.map((business, index) => {
      return {
        business_id: business.business_id || business.BusinessID || business.id || `temp-${index}`,
        business_name: business.business_name || business.BusinessName || business.name || 'Unnamed',
        industry_type: business.industry_type || business.IndustryType || business.industry || 'Not specified',
        client_id: business.client_id || business.ClientID || business.clientId || 'Not specified'
      };
    });

    console.log('🎯 Final formatted businesses:', formatted);
    setBusinesses(formatted);
  };

  // Format and set systems
  const formatAndSetSystems = (data) => {
    let systemsArray = [];

    if (Array.isArray(data)) {
      systemsArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) systemsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) systemsArray = data.resultSet;
      else if (Array.isArray(data.data)) systemsArray = data.data;
      else if (Array.isArray(data.responseBody)) systemsArray = data.responseBody;
      else if (Array.isArray(data.systems)) systemsArray = data.systems;
      else if (data.system_id) systemsArray = [data];
    }

    const formatted = systemsArray.map((system, index) => ({
      system_id: system.system_id || system.SystemID || system.id || `temp-${index}`,
      system_name: system.system_name || system.SystemName || system.name || 'Unnamed',
      description: system.description || system.Description || 'No description',
      status: system.status || system.Status || "Active"
    }));

    setSystems(formatted);
  };

  // Format and set clients
  const formatAndSetClients = (data) => {
    let clientsArray = [];

    if (Array.isArray(data)) {
      clientsArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) clientsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) clientsArray = data.resultSet;
      else if (Array.isArray(data.data)) clientsArray = data.data;
      else if (Array.isArray(data.responseBody)) clientsArray = data.responseBody;
      else if (Array.isArray(data.clients)) clientsArray = data.clients;
      else if (data.client_id) clientsArray = [data];
    }

    const formatted = clientsArray.map((client, index) => ({
      client_id: client.client_id || client.ClientID || client.id || `temp-${index}`,
      client_name: client.client_name || client.ClientName || client.name || 'Unnamed',
      client_email: client.client_email || client.ClientEmail || client.email || '',
      client_contact: client.client_contact || client.ClientContact || client.contact || '',
      status: client.status || client.Status || "Active"
    }));

    setClients(formatted);
  };

  // Format and set all licenses
  const formatAndSetAllLicenses = (data) => {
    let licensesArray = [];

    if (Array.isArray(data)) {
      licensesArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) licensesArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) licensesArray = data.resultSet;
      else if (Array.isArray(data.data)) licensesArray = data.data;
      else if (Array.isArray(data.responseBody)) licensesArray = data.responseBody;
      else if (Array.isArray(data.licenses)) licensesArray = data.licenses;
      else if (data.license_id) licensesArray = [data];
    }

    const formatted = licensesArray.map((license, index) => ({
      license_id: license.license_id || license.LicenseID || license.id || `temp-${index}`,
      business_id: license.business_id || license.BusinessID || 'Not specified',
      system_id: license.system_id || license.SystemID || 'Not specified',
      license_key: license.license_key || license.LicenseKey || `KEY-${index}`,
      issue_date: license.issue_date || license.IssueDate || new Date().toISOString().split('T')[0],
      expiry_date: license.expiry_date || license.ExpiryDate || new Date().toISOString().split('T')[0],
      subscription_type: license.subscription_type || license.SubscriptionType || 'yearly',
      max_users: license.max_users || license.MaxUsers || 1,
      status: formatLicenseStatus(license.status || license.Status || "A")
    }));

    console.log('🎯 All licenses loaded:', formatted);
    setAllLicenses(formatted);
  };

  // Format license status
  const formatLicenseStatus = (status) => {
    if (status === "A" || status === "Active" || status === "active") {
      return "Active";
    } else if (status === "I" || status === "Inactive" || status === "inactive") {
      return "Inactive";
    } else if (status === "E" || status === "Expired" || status === "expired") {
      return "Expired";
    } else if (status === "P" || status === "Pending" || status === "pending") {
      return "Pending";
    } else {
      return "Active";
    }
  };

  // Format and set all payments
  const formatAndSetAllPayments = (data) => {
    let paymentsArray = [];

    if (Array.isArray(data)) {
      paymentsArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) paymentsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) paymentsArray = data.resultSet;
      else if (Array.isArray(data.data)) paymentsArray = data.data;
      else if (Array.isArray(data.responseBody)) paymentsArray = data.responseBody;
      else if (Array.isArray(data.payments)) paymentsArray = data.payments;
      else if (data.payment_id) paymentsArray = [data];
    }

    const formatted = paymentsArray.map((payment, index) => ({
      payment_id: payment.payment_id || payment.PaymentID || payment.id || `temp-${index}`,
      license_id: payment.license_id || payment.LicenseID || 'Not specified',
      invoice_no: payment.invoice_no || payment.InvoiceNo || `INV-${index}`,
      payment_plan: payment.payment_plan || payment.PaymentPlan || 'monthly',
      total_amount: payment.total_amount || payment.TotalAmount || 0,
      paid_amount: payment.paid_amount || payment.PaidAmount || 0,
      balance_amount: payment.balance_amount || payment.BalanceAmount || 0,
      status: formatPaymentStatus(payment.status || payment.Status || "U"),
      created_date: payment.created_date || payment.CreatedDate || new Date().toISOString().split('T')[0]
    }));

    console.log('💰 All payments loaded:', formatted);
    setAllPayments(formatted);
  };

  // Format payment status
  const formatPaymentStatus = (status) => {
    if (status === "F" || status === "Paid") return "Paid";
    if (status === "P" || status === "Partial") return "Partial";
    if (status === "U" || status === "Unpaid") return "Unpaid";
    return status;
  };

  // Format payment details
  const formatPaymentDetails = (data) => {
    let detailsArray = [];

    if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) detailsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) detailsArray = data.resultSet;
      else if (Array.isArray(data.data)) detailsArray = data.data;
      else if (Array.isArray(data.responseBody)) detailsArray = data.responseBody;
      else if (Array.isArray(data.paymentDetails)) detailsArray = data.paymentDetails;
      else if (data.payment_date) detailsArray = [data];
    }

    const formatted = detailsArray.map((detail, index) => ({
      payment_date: detail.payment_date || detail.PaymentDate || new Date().toISOString().split('T')[0],
      payment_method: detail.payment_method || detail.PaymentMethod || 'Cash',
      reference_no: detail.reference_no || detail.ReferenceNo || `REF-${Date.now()}-${index}`,
      amount: detail.amount || detail.Amount || '0.00',
      remarks: detail.remarks || detail.Remarks || 'No remarks'
    }));

    setPaymentDetails(formatted);
  };

  // Format and set logs
  const formatAndSetLogs = (data) => {
    console.log('🔄 Formatting logs data:', data);
    
    const formatted = formatLogsForFiltering(data);
    console.log('🎯 Formatted logs:', formatted);
    setLogs(formatted);
  };

  // API calls
  const loadBusinesses = async () => {
    try {
      console.log('🚀 Loading businesses...');
      await dispatch(GetAllBusinessesAction());
    } catch (error) {
      console.error('❌ Error loading businesses:', error);
    }
  };

  const loadClients = async () => {
    try {
      await dispatch(GetAllClientsAction());
    } catch (error) {
      console.error('❌ Error loading clients:', error);
    }
  };

  const loadAllLicenses = async () => {
    try {
      console.log('🚀 Loading all licenses...');
      await dispatch(GetAllLicensesAction());
    } catch (error) {
      console.error('❌ Error loading all licenses:', error);
    }
  };

  const loadAllPayments = async () => {
    try {
      console.log('💰 Loading all payments...');
      await dispatch(GetAllPaymentsAction());
    } catch (error) {
      console.error('❌ Error loading all payments:', error);
    }
  };

  // Load business logs
  const loadBusinessLogs = async () => {
    try {
      setLogsLoading(true);
      setLogsError('');
      
      if (selectedBusiness) {
        console.log(`📊 Loading logs for business: ${selectedBusiness.business_name}`);
        await dispatch(GetBusinessInteractionLogAction(
          selectedBusiness.business_id,
          selectedBusiness.client_id
        ));
      }
    } catch (error) {
      console.error('❌ Error loading business logs:', error);
      setLogsError('Failed to load business logs. Please try again.');
    } finally {
      setLogsLoading(false);
    }
  };

  // Handle add business
  const handleAddBusiness = async (e) => {
    e.preventDefault();

    // Validate form inputs
    if (!validateBusinessForm()) {
      return;
    }

    try {
      console.log('➕ Adding business:', newBusiness);
      await dispatch(AddBusinessAction(newBusiness));
    } catch (error) {
      console.error('❌ Error adding business:', error);
    }
  };

  // Handle input changes with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setNewBusiness(prev => ({ 
      ...prev, 
      [name]: value 
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear duplicate name error when business name changes
    if (name === 'business_name' && duplicateNameError) {
      setDuplicateNameError('');
    }
  };

  // Handle license click
  const handleLicenseClick = (business) => {
    setSelectedBusiness(business);
    setShowLicenses(true);
    
    const businessLicenses = allLicenses.filter(
      license => license.business_id === business.business_id
    );
    
    console.log(`🔍 Found ${businessLicenses.length} licenses for business:`, business.business_name);
    setBusinessLicenses(businessLicenses);
  };

  // Handle payment click
  const handlePaymentClick = async (license) => {
    setSelectedLicense(license);
    setShowPayment(true);
    setPaymentData(null);
    setPaymentDetails([]);
    setPaymentLoading(true);
    
    try {
      // Find payment for this license
      const paymentForLicense = allPayments.find(payment => 
        payment.license_id.toString() === license.license_id.toString()
      );

      if (paymentForLicense && paymentForLicense.payment_id) {
        console.log(`💰 Found payment for license ${license.license_id}:`, paymentForLicense);
        
        // Load payment master data
        await dispatch(GetPaymentByIdAction(paymentForLicense.payment_id));
        
        // Load payment details
        await dispatch(GetPaymentDetailsByIdAction(paymentForLicense.payment_id));
      } else {
        console.log(`❌ No payment found for license ${license.license_id}`);
        setPaymentData({
          payment_id: `PMT-${Date.now()}`,
          invoice_no: `INV-${Date.now()}`,
          license_id: license.license_id,
          payment_plan: license.subscription_type || 'monthly',
          total_amount: '0.00',
          paid_amount: '0.00',
          balance_amount: '0.00',
          status: 'Unpaid'
        });
        setPaymentDetails([{
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: 'Not set',
          reference_no: `REF-${Date.now()}`,
          amount: '0.00',
          remarks: 'No payment records'
        }]);
      }
    } catch (error) {
      console.error('❌ Error loading payment data:', error);
      setPaymentData({
        payment_id: `PMT-${Date.now()}`,
        invoice_no: `INV-${Date.now()}`,
        license_id: license.license_id,
        payment_plan: license.subscription_type || 'monthly',
        total_amount: '0.00',
        paid_amount: '0.00',
        balance_amount: '0.00',
        status: 'Unpaid'
      });
      setPaymentDetails([{
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Not set',
        reference_no: `REF-${Date.now()}`,
        amount: '0.00',
        remarks: 'Error loading payment data'
      }]);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle log click
  const handleLogClick = (business) => {
    setSelectedBusiness(business);
    setShowLog(true);
    
    // Clear previous filtered logs
    setFilteredLogs([]);
    
    // Load logs for this business
    loadBusinessLogs();
  };

  // Handle add log
  const handleAddLog = async (logData) => {
    try {
      console.log('🔄 Adding new log:', logData);
      await dispatch(AddBusinessLogAction(logData));
      
      setLogsError('');
      return true;
    } catch (error) {
      console.error('❌ Error adding log:', error);
      setLogsError('Failed to add log. Please try again.');
      return false;
    }
  };

  // Handle close add form
  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setNewBusiness({
      business_name: '',
      industry_type: '',
      client_id: ''
    });
    setValidationErrors({});
    setDuplicateNameError('');
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Filter businesses
  const filteredBusinesses = businesses.filter(business =>
    business.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    business.industry_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (business.business_id && business.business_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (business.client_id && business.client_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBusinesses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPagesCount = Math.ceil(filteredBusinesses.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPagesCount) {
      setCurrentPage(pageNumber);
    }
  };

  // Conditional rendering for different views
  if (showPayment) {
    return (
      <div className="p-4 lg:p-6">
        <PaymentInformationView
          selectedBusiness={selectedBusiness}
          selectedLicense={selectedLicense}
          paymentData={paymentData}
          paymentDetails={paymentDetails}
          systems={systems}
          paymentLoading={paymentLoading}
          paymentByIdLoading={paymentByIdLoading}
          paymentDetailsLoading={paymentDetailsLoading}
          paymentByIdError={paymentByIdError}
          paymentDetailsError={paymentDetailsError}
          onBack={() => setShowPayment(false)}
        />
      </div>
    );
  }

  if (showLog) {
    return (
      <div className="p-4 lg:p-6">
        <BusinessLogView
          business={selectedBusiness}
          onBack={() => setShowLog(false)}
          filteredLogs={filteredLogs}
          logsLoading={logsLoading}
          logsError={logsError}
          logsErrorState={logsErrorState}
          logsLoadingState={logsLoadingState}
          addLogLoading={addLogLoading}
          addLogError={addLogError}
          onAddLog={handleAddLog}
        />
      </div>
    );
  }

  if (showLicenses) {
    return (
      <div className="p-4 lg:p-6">
        <BusinessLicensesView
          business={selectedBusiness}
          onBack={() => setShowLicenses(false)}
          businessLicenses={businessLicenses}
          systems={systems}
          allLicensesLoading={allLicensesLoading}
          onPaymentClick={handlePaymentClick}
        />
      </div>
    );
  }

  // Main Business Collection View
  return (
    <div className="h-full flex flex-col p-4 lg:p-6">
      {/* Header and Button Row - Combined */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Business Collection</h1>
          <p className="text-sm md:text-base text-gray-600">Manage your business collections and licenses</p>
        </div>
        
        <button 
          onClick={() => setShowAddForm(true)}
          disabled={addBusinessLoading || businessesLoading}
          className="bg-blue-500 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2 font-medium disabled:bg-blue-300 disabled:cursor-not-allowed"
        >
          <Plus size={18} />
          <span className="text-sm md:text-base">{addBusinessLoading ? 'Adding...' : 'Add New Business'}</span>
        </button>
      </div>

      {/* Error Messages */}
      {(businessesError || addBusinessError || allLicensesError) && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm md:text-base">
          <strong>Error:</strong> {businessesError || addBusinessError || allLicensesError}
        </div>
      )}

      {/* Add Business Form Modal */}
      <AddBusinessModal
        isOpen={showAddForm}
        onClose={handleCloseAddForm}
        onSubmit={handleAddBusiness}
        newBusiness={newBusiness}
        onChange={handleInputChange}
        clients={clients}
        isLoading={addBusinessLoading}
        validationErrors={validationErrors}
        duplicateNameError={duplicateNameError}
        addBusinessError={addBusinessError}
      />

      {/* Loading State */}
      {(businessesLoading) && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600 text-sm md:text-base">Loading businesses...</p>
        </div>
      )}

      {/* Businesses Table Container */}
      {!businessesLoading && (
        <div className="flex-1 min-h-0 overflow-x-auto">
          <BusinessTable
            businesses={businesses}
            filteredBusinesses={filteredBusinesses}
            currentItems={currentItems}
            clients={clients}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalPages={totalPagesCount}
            onLicenseClick={handleLicenseClick}
            onLogClick={handleLogClick}
            onPageChange={handlePageChange}
            startIndex={indexOfFirstItem}
            endIndex={indexOfLastItem}
            searchTerm={searchTerm}
          />
        </div>
      )}
    </div>
  );
};

export default BusinessCollection;